/**
 * Standalone Add to Cart Button Component
 *
 * Allows placing add-to-cart buttons anywhere with minimal attributes.
 * Includes full validation, error handling, and cart integration.
 *
 * Usage:
 * <button class="m-standalone-atc"
 *         data-variant-id="12345"
 *         data-quantity="1">
 *   Add to Cart
 * </button>
 */

class StandaloneAddToCart {
  constructor() {
    this.buttonSelector = '.m-standalone-atc';
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const button = event.target.closest(this.buttonSelector);
    if (!button) return;

    event.preventDefault();
    this.addToCart(button);
  }

  async addToCart(button) {
    // Prevent duplicate requests
    if (button.disabled || button.classList.contains('loading')) {
      return;
    }

    // Extract data attributes
    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity) || 1;
    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const showNotification = button.dataset.showNotification !== 'false';
    const openCartDrawer = button.dataset.openCartDrawer !== 'false';

    // Validation
    if (!variantId) {
      this.showError(button, 'Missing variant ID. Please add data-variant-id attribute.');
      return;
    }

    if (quantity < 1) {
      this.showError(button, 'Quantity must be at least 1');
      return;
    }

    // Set loading state
    this.setLoadingState(button, true);

    try {
      // Build form data
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      // Add selling plan if specified
      if (button.dataset.sellingPlanId) {
        formData.append('selling_plan', button.dataset.sellingPlanId);
      }

      // Add product properties if specified
      if (button.dataset.properties) {
        try {
          const properties = JSON.parse(button.dataset.properties);
          Object.entries(properties).forEach(([key, value]) => {
            formData.append(`properties[${key}]`, value);
          });
        } catch (e) {
          console.warn('[Standalone ATC] Invalid properties JSON:', e);
        }
      }

      // Get cart sections to update (optional - skip if causes issues)
      try {
        const sections = this.getCartSections();
        if (sections.length > 0) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }
      } catch (e) {
        console.warn('[Standalone ATC] Could not get sections:', e);
      }

      // Make request
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('Invalid response from server');
      }

      if (response.ok) {
        // Success
        await this.handleSuccess(button, data, {
          showNotification,
          openCartDrawer,
          productHandle
        });
      } else {
        // Server error
        this.handleError(button, data);
      }
    } catch (error) {
      // Network error
      this.handleError(button, {
        message: 'Network error. Please try again.',
        error: error.message
      });
    } finally {
      this.setLoadingState(button, false);
    }
  }

  async handleSuccess(button, response, options = {}) {
    const { showNotification, openCartDrawer, productHandle } = options;

    // IMPORTANT: Always fetch full cart state to ensure we have complete data
    // The /cart/add.js response doesn't include full cart items
    let cartWithSections = response;

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      if (cartResponse.ok) {
        const fullCart = await cartResponse.json();

        // Fetch sections HTML separately if needed
        if (!response.sections || Object.keys(response.sections || {}).length === 0) {
          const sectionsToFetch = this.getCartSections();

          if (sectionsToFetch.length > 0) {
            const sectionsUrl = `${window.Shopify.routes.root}?sections=${sectionsToFetch.join(',')}`;
            const sectionsResponse = await fetch(sectionsUrl);
            if (sectionsResponse.ok) {
              const sectionsData = await sectionsResponse.json();
              fullCart.sections = sectionsData;
            }
          }
        } else {
          // Use sections from add response
          fullCart.sections = response.sections;
        }

        cartWithSections = fullCart;
      }
    } catch (e) {
      console.warn('[Standalone ATC] Could not fetch full cart:', e);
      // Fallback: use response as is
    }

    // Emit cart update event
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        cart: cartWithSections
      });
    }

    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('product-ajax:added', {
      detail: {
        product: response,
        source: 'standalone-atc',
        button: button
      }
    }));

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        cart: cartWithSections,
        fromStandaloneATC: true
      }
    }));

    // Show success notification
    if (showNotification && window.MinimogTheme && window.MinimogTheme.Notification) {
      const message = window.MinimogStrings?.itemAdded || 'Item added to cart';
      window.MinimogTheme.Notification.show({
        type: 'success',
        message: message,
        last: 3000
      });
    }

    // Open cart drawer
    if (openCartDrawer) {
      const cartDrawer = document.querySelector('m-cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    // Add success class for CSS animation
    button.classList.add('success');
    setTimeout(() => {
      button.classList.remove('success');
    }, 2000);
  }

  handleError(button, errorData) {
    const errorMessage = errorData.message ||
                        errorData.description ||
                        'Unable to add to cart';

    console.error('[Standalone ATC] Error:', errorData);

    // Emit error event
    if (window.MinimogEvents && window.MinimogTheme) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartError, {
        source: 'standalone-atc',
        errors: errorMessage,
        data: errorData
      });
    }

    // Show error notification
    if (window.MinimogTheme && window.MinimogTheme.Notification) {
      window.MinimogTheme.Notification.show({
        type: 'error',
        message: errorMessage,
        last: 5000
      });
    } else {
      // Fallback: show alert
      alert(errorMessage);
    }

    // Add error class for CSS animation
    button.classList.add('error');
    setTimeout(() => {
      button.classList.remove('error');
    }, 2000);
  }

  showError(button, message) {
    console.error('[Standalone ATC]', message);

    if (window.MinimogTheme && window.MinimogTheme.Notification) {
      window.MinimogTheme.Notification.show({
        type: 'error',
        message: message,
        last: 3000
      });
    } else {
      alert(message);
    }
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.classList.add('loading');

      // Store original text
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }

      // Add spinner if theme supports it
      const spinner = button.querySelector('.m-spinner-icon');
      if (spinner) {
        spinner.style.display = 'inline-block';
      }
    } else {
      button.disabled = false;
      button.classList.remove('loading');

      // Restore original text
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
      }

      const spinner = button.querySelector('.m-spinner-icon');
      if (spinner) {
        spinner.style.display = 'none';
      }
    }
  }

  getCartSections() {
    try {
      const sections = [];

      // Try to dispatch event to collect sections
      try {
        const sectionsEvent = new CustomEvent('cart:grouped-sections', {
          detail: { sections }
        });
        document.dispatchEvent(sectionsEvent);
      } catch (e) {
        console.warn('[Standalone ATC] Could not dispatch sections event:', e);
      }

      // Add default sections if none collected
      if (sections.length === 0) {
        // Check for cart drawer
        if (document.querySelector('m-cart-drawer')) {
          sections.push('cart-drawer');
        }
        // Check for cart page
        if (document.querySelector('m-cart-items')) {
          sections.push('cart-items');
        }
        // Fallback: just update cart icon count
        if (sections.length === 0) {
          sections.push('header');
        }
      }

      return sections;
    } catch (e) {
      console.warn('[Standalone ATC] Error getting cart sections:', e);
      return []; // Return empty array on error
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StandaloneAddToCart();
  });
} else {
  new StandaloneAddToCart();
}
